/**
 * @file permission_utils.js
 * @description Pure utility functions for computing permission keys.
 * This file has NO dependency on request.js, so it can be safely imported
 * by any module without triggering circular dependency issues.
 */


/**
 * 将路由 name 转为合法的权限标识符（与 export_routes.js 保持一致）。
 * 所有非字母数字字符统一替换为连字符 '-'。
 * @param {string} name - 路由 name
 * @returns {string} 权限标识符
 */
export function to_permission_identifier(name) {
    return name.replace(/[^a-zA-Z0-9]/g, '-')
}


/**
 * 根据路由配置项计算权限 key。
 * 有 children 的项使用 group 格式，叶子节点使用 parent_name + name 格式。
 *
 * @param {Object} item - 路由配置项（含 name / children）
 * @param {string} group_name - 父级路由 name（叶子节点时需要）
 * @returns {string} 权限 key
 */
export function compute_item_permission_key(item, group_name) {
    const has_children = item['children'] && item['children'].length > 0;
    if (has_children) {
        return 'view-' + to_permission_identifier(item['name']) + '-view-' + to_permission_identifier(item['name']) + '-group'
    }
    return 'view-' + to_permission_identifier(group_name) + '-view-' + to_permission_identifier(item['name'])
}


/**
 * 根据 Vue Router matched record 计算权限 key，用于路由守卫拦截。
 * 逻辑与 compute_item_permission_key 保持一致。
 *
 * @param {Object} matched - Vue Router 的 to 对象（含 matched 数组）
 * @returns {string|null} 权限 key，无法计算时返回 null
 */
export function compute_route_permission_key(matched) {
    const current = matched.matched[matched.matched.length - 1];
    if (!current || !current.name) { return null }

    const has_children = current.children && current.children.length > 0;
    if (has_children) {
        return 'view-' + to_permission_identifier(current.name) + '-view-' + to_permission_identifier(current.name) + '-group'
    }

    // 叶子节点：取最近的有名祖先作为 group
    let parent_name = null;
    for (let i = matched.matched.length - 2; i >= 0; i--) {
        if (matched.matched[i].name) {
            parent_name = matched.matched[i].name
            break
        }
    }
    if (!parent_name) { return null }

    return 'view-' + to_permission_identifier(parent_name) + '-view-' + to_permission_identifier(current.name)
}
